<template>
  <div class="category-scores-chart">
    <canvas ref="chartCanvas"></canvas>
  </div>
</template>

<script>
import { ref, onMounted, watch } from 'vue';
import Chart from 'chart.js/auto';

export default {
  name: 'CategoryScoresChart',
  props: {
    scores: {
      type: Object,
      required: true
    },
    chartType: {
      type: String,
      default: 'radar' // Options: 'radar', 'bar', 'polarArea'
    }
  },
  setup(props) {
    const chartCanvas = ref(null);
    let chartInstance = null;
    
    const colors = {
      primary: 'rgba(13, 110, 253, 0.8)',
      primaryLight: 'rgba(13, 110, 253, 0.2)',
      success: 'rgba(40, 167, 69, 0.8)',
      successLight: 'rgba(40, 167, 69, 0.2)',
      info: 'rgba(23, 162, 184, 0.8)',
      infoLight: 'rgba(23, 162, 184, 0.2)',
      warning: 'rgba(255, 193, 7, 0.8)',
      warningLight: 'rgba(255, 193, 7, 0.2)',
      danger: 'rgba(220, 53, 69, 0.8)',
      dangerLight: 'rgba(220, 53, 69, 0.2)'
    };
    
    const getChartConfig = () => {
      const labels = Object.keys(props.scores);
      const data = Object.values(props.scores);
      
      if (props.chartType === 'radar') {
        return {
          type: 'radar',
          data: {
            labels: labels,
            datasets: [{
              label: 'Score',
              data: data,
              backgroundColor: colors.primaryLight,
              borderColor: colors.primary,
              borderWidth: 2,
              pointBackgroundColor: colors.primary,
              pointBorderColor: '#fff',
              pointHoverBackgroundColor: '#fff',
              pointHoverBorderColor: colors.primary
            }]
          },
          options: {
            responsive: true,
            maintainAspectRatio: false,
            plugins: {
              legend: {
                display: false
              },
              tooltip: {
                callbacks: {
                  label: function(context) {
                    return context.label + ': ' + context.parsed.r + '%';
                  }
                }
              }
            },
            scales: {
              r: {
                angleLines: {
                  display: true,
                  color: 'rgba(0, 0, 0, 0.1)'
                },
                suggestedMin: 0,
                suggestedMax: 100,
                ticks: {
                  stepSize: 20,
                  callback: function(value) {
                    return value + '%';
                  }
                }
              }
            }
          }
        };
      } else if (props.chartType === 'bar') {
        return {
          type: 'bar',
          data: {
            labels: labels,
            datasets: [{
              label: 'Score',
              data: data,
              backgroundColor: data.map(score => {
                if (score >= 90) return colors.success;
                if (score >= 75) return colors.primary;
                if (score >= 60) return colors.info;
                if (score >= 40) return colors.warning;
                return colors.danger;
              }),
              borderColor: data.map(score => {
                if (score >= 90) return colors.success;
                if (score >= 75) return colors.primary;
                if (score >= 60) return colors.info;
                if (score >= 40) return colors.warning;
                return colors.danger;
              }),
              borderWidth: 1
            }]
          },
          options: {
            responsive: true,
            maintainAspectRatio: false,
            plugins: {
              legend: {
                display: false
              },
              tooltip: {
                callbacks: {
                  label: function(context) {
                    return 'Score: ' + context.parsed.y + '%';
                  }
                }
              }
            },
            scales: {
              y: {
                beginAtZero: true,
                max: 100,
                ticks: {
                  callback: function(value) {
                    return value + '%';
                  }
                }
              }
            }
          }
        };
      } else if (props.chartType === 'polarArea') {
        return {
          type: 'polarArea',
          data: {
            labels: labels,
            datasets: [{
              label: 'Score',
              data: data,
              backgroundColor: [
                colors.primaryLight,
                colors.successLight,
                colors.infoLight,
                colors.warningLight,
                colors.dangerLight
              ],
              borderColor: [
                colors.primary,
                colors.success,
                colors.info,
                colors.warning,
                colors.danger
              ],
              borderWidth: 2
            }]
          },
          options: {
            responsive: true,
            maintainAspectRatio: false,
            plugins: {
              legend: {
                position: 'right'
              },
              tooltip: {
                callbacks: {
                  label: function(context) {
                    return context.label + ': ' + context.parsed.r + '%';
                  }
                }
              }
            },
            scales: {
              r: {
                beginAtZero: true,
                max: 100,
                ticks: {
                  callback: function(value) {
                    return value + '%';
                  }
                }
              }
            }
          }
        };
      }
      
      // Default to bar chart
      return getChartConfig('bar');
    };
    
    const createChart = () => {
      if (chartInstance) {
        chartInstance.destroy();
      }
      
      if (!chartCanvas.value || Object.keys(props.scores).length === 0) {
        return;
      }
      
      const ctx = chartCanvas.value.getContext('2d');
      chartInstance = new Chart(ctx, getChartConfig());
    };
    
    const updateChart = () => {
      if (!chartInstance) {
        createChart();
        return;
      }
      
      const labels = Object.keys(props.scores);
      const data = Object.values(props.scores);
      
      chartInstance.data.labels = labels;
      chartInstance.data.datasets[0].data = data;
      
      if (props.chartType === 'bar') {
        chartInstance.data.datasets[0].backgroundColor = data.map(score => {
          if (score >= 90) return colors.success;
          if (score >= 75) return colors.primary;
          if (score >= 60) return colors.info;
          if (score >= 40) return colors.warning;
          return colors.danger;
        });
      }
      
      chartInstance.update();
    };
    
    onMounted(() => {
      createChart();
    });
    
    watch(() => props.scores, () => {
      updateChart();
    }, { deep: true });
    
    watch(() => props.chartType, () => {
      createChart();
    });
    
    return {
      chartCanvas
    };
  }
};
</script>

<style scoped>
.category-scores-chart {
  position: relative;
  height: 300px;
  width: 100%;
}

canvas {
  max-height: 300px;
}
</style>
